/**
 * Đồng, written and read.
 *
 * Money in this product is an integer number of đồng — the database column is `bigint` and the contract
 * is `z.number().int()`. So there is no float anywhere in this file: no division, no `toFixed`, no
 * rounding, no `parseFloat`. A formatted amount is the integer's own digits with group separators, and a
 * parsed amount is the digits the user typed, converted once.
 *
 * The grouping is done on the digit string rather than by `Intl.NumberFormat`, for two reasons. The
 * locale data would format `1.000.000` the same way today but is a runtime that can be missing or
 * configured differently; and an `Intl` formatter returns a string whose separators and spacing this
 * product would then have to accept in every locale. One format, written here, is what the rest of the
 * app already does with dates.
 */

/** The group separator Vietnamese uses: `.` — `1.000.000 đ`. */
const GROUP_SEPARATOR = ".";

/**
 * Group a run of digits: `1000000` → `1.000.000`. The one grouping in the app, so a figure and a field
 * being typed cannot disagree about where the separators go.
 */
function group(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEPARATOR);
}

/**
 * An integer number of đồng, grouped and labelled: `1000000` → `1.000.000 đ`.
 *
 * The sign is kept outside the grouping, so a negative pot reads `-500.000 đ` — a number, not a warning.
 */
export function formatVnd(amount: number): string {
  const sign = amount < 0 ? "-" : "";
  // `Math.abs` on an integer is exact; the grouping is applied to the digit string, never to a number.
  return `${sign}${group(String(Math.abs(amount)))} đ`;
}

/**
 * What the amount field shows while it is being typed: the digits the user entered, grouped, and nothing
 * else. The ` đ` belongs to a rendered figure, not to a value being edited, and the leading zeros a
 * number does not have are dropped as they are typed (`05` is `5`). The separators are presentation only:
 * `parseVndInput` strips them back to the integer the field will submit.
 */
export function formatAmountInput(text: string): string {
  const digits = text.replace(/\D/g, "").replace(/^0+(?=\d)/, "");
  return group(digits);
}

/** The largest amount a suggestion will offer. Above it there is nothing left to grow into. */
export const AMOUNT_SUGGESTION_CAP = 100_000_000;

/**
 * Amounts to offer under the field, derived from what the user has typed. Suggestions about the *number*,
 * never presented as a fact about the trip.
 *
 * A typed amount below a thousand is read as a shorthand for hundreds through hundred-thousands
 * (`5` → 5.000, 50.000, 500.000); an amount of a thousand or more is carried up one and two places.
 * The `>= 1000` branch **multiplies** rather than rounding: `123456` offers `1.234.560` and `12.345.600`,
 * not `120.000`/`1.200.000`. The ticket's prose said "rounded", but scaling is the behaviour the reference
 * code specified and what is kept — the suggestion is the amount on screen carried up a place, which a
 * user can predict from what they typed.
 *
 * A multiple is offered only while it stays at or under `AMOUNT_SUGGESTION_CAP`, so an amount above
 * 10.000.000 offers nothing at all: both multiples exceed the cap and the result is the empty array.
 *
 * **An empty field offers nothing**, and so does a lone `0`. There is no product default to fall back
 * on: nothing typed is not a number to carry up a place, and `0 × 1000` is not an amount anyone means.
 * The sheet renders no chip row at all for an empty result, which is what keeps the form short — an
 * empty strip or a fallback to fixed popular amounts would put four chips under a field its user has not
 * started filling in. Suggesting nothing is also the honest answer above the cap: suggesting a smaller
 * amount than the one already typed would be worse than suggesting none.
 *
 * What it reads ignores grouping and units, so `50.000` and `50000` name the same number.
 */
export function getAmountSuggestions(inputStr: string): number[] {
  const cleanNum = parseInt(inputStr.replace(/\D/g, ""), 10);
  // Nothing typed, or a lone zero: no number to carry up, so nothing to suggest.
  if (isNaN(cleanNum) || cleanNum === 0) return [];
  if (cleanNum < 1000) return [cleanNum * 1000, cleanNum * 10000, cleanNum * 100000];
  const out: number[] = [];
  if (cleanNum * 10 <= AMOUNT_SUGGESTION_CAP) out.push(cleanNum * 10);
  if (cleanNum * 100 <= AMOUNT_SUGGESTION_CAP) out.push(cleanNum * 100);
  return out;
}

/**
 * Read an amount the user typed: an integer number of đồng, or null when the text is not one.
 *
 * Grouping separators (`.`, `,`, spaces) and a trailing `đ`/`vnd`/`₫` are accepted and ignored, because a
 * user pasting `1.000.000` means a million — but `1.5` is refused rather than truncated to `1`: this
 * product has no đồng fractions, and silently dropping them would record an amount nobody typed. The
 * parser never produces a float; it returns the integer the digits name, or null and lets the caller say
 * the field is empty.
 */
export function parseVndInput(text: string): number | null {
  const cleaned = text
    .trim()
    .toLowerCase()
    .replace(/(đ|vnd|₫)$/u, "")
    .replace(/[\s.,]/g, "");
  if (cleaned === "" || !/^\d+$/.test(cleaned)) return null;
  const amount = Number(cleaned);
  // Beyond this the integer is no longer exactly representable, so the field is not accepted rather
  // than silently rounded to a neighbouring đồng.
  if (!Number.isSafeInteger(amount)) return null;
  return amount;
}
